import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDemoData } from "@/hooks/useDemoData";
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { ChevronDown, ChevronRight } from "lucide-react";
import EditDriverModal from "../modals/EditDriverModal";

export default function DriversTab() {
  const { drivers, trucks, addDriver, updateDriver } = useDemoData();

  const [expanded, setExpanded] = useState<string[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  
  // Get active/inactive drivers based on their assigned truck status
  const activeDrivers = drivers.filter((d) => {
    const truck = trucks.find(t => t.id === d.currentTruck);
    return truck && (truck.status === "Active" || truck.status === "Loading");
  });
  
  const inactiveDrivers = drivers.filter((d) => {
    const truck = trucks.find(t => t.id === d.currentTruck);
    return !truck || (truck.status !== "Active" && truck.status !== "Loading");
  });

  const toggle = (id: string) =>
    setExpanded((e) =>
      e.includes(id) ? e.filter((x) => x !== id) : [...e, id]
    );

  const handleEditClick = (driver: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDriver(driver);
    setEditModalOpen(true);
  };

  const handleSaveEdit = (driverId: string, data: any) => {
    updateDriver(driverId, data);
  };

  const { register, handleSubmit, reset } = useForm();

  const onSubmit = (data: any) => {
    addDriver({
      id: data.id,
      name: data.name,
      currentTruck: data.currentTruck || "",
      contact: data.contact || Math.floor(Math.random() * 1000000000),
    });
    reset();
  };

  return (
    <div className="gap-6 p-4 space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-4">Active Drivers</h3>
          </CardHeader>

          <CardContent>
            <Table>
              <TableHeader className="bg-gray-100">
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Driver ID</TableHead>
                  <TableHead className="text-center">Contact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeDrivers.map((d) => (
                  <React.Fragment key={d.id}>
                    <TableRow
                      className={`hover:bg-gray-200 cursor-pointer ${expanded.includes(d.id) ? "bg-gray-200" : ""}`}
                      onClick={() => toggle(d.id)}
                    >
                      <TableCell>
                        {expanded.includes(d.id) ? (
                          <ChevronDown className="h-4 w-4 inline mr-2" />
                        ) : (
                          <ChevronRight className="h-4 w-4 inline mr-2" />
                        )}
                        {d.name}
                      </TableCell>
                      <TableCell>{d.id}</TableCell>
                      <TableCell className="text-center">{d.contact}</TableCell>
                    </TableRow>
                    {expanded.includes(d.id) && (
                      <TableRow className="">
                        <TableCell colSpan={3} className="p-0 bg-purple-300">
                          <div className="p-4 bg-gray-200 border-t">
                            <div className="grid grid-cols-3 gap-4 mb-3">
                              <p className="text-left">
                                <strong>Current Truck</strong>
                              </p>
                              <p className="text-center">
                                <strong>Truck Model</strong>
                              </p>
                              <p className="text-center">
                                <strong>Status</strong>
                              </p>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mb-3">
                              <p className="text-left">{d.currentTruck}</p>
                              <p className="text-center">
                                {trucks.find(t => t.id === d.currentTruck)?.model || "N/A"}
                              </p>
                              <p className="text-center">
                                {trucks.find(t => t.id === d.currentTruck)?.status || "N/A"}
                              </p>
                            </div>
                            <Button 
                              className="w-full"
                              onClick={(e) => handleEditClick(d, e)}
                            >
                              Edit
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-4">Inactive Drivers</h3>
          </CardHeader>

          <CardContent>
            <Table>
              <TableHeader className="bg-gray-100">
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Driver ID</TableHead>
                  <TableHead className="text-center">Contact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inactiveDrivers.map((d) => (
                  <React.Fragment key={d.id}>
                    <TableRow
                      className={`hover:bg-gray-200 cursor-pointer ${expanded.includes(d.id) ? "bg-gray-200" : ""}`}
                      onClick={() => toggle(d.id)}
                    >
                      <TableCell>
                        {expanded.includes(d.id) ? (
                          <ChevronDown className="h-4 w-4 inline mr-2" />
                        ) : (
                          <ChevronRight className="h-4 w-4 inline mr-2" />
                        )}
                        {d.name}
                      </TableCell>
                      <TableCell>{d.id}</TableCell>
                      <TableCell className="text-center">{d.contact}</TableCell>
                    </TableRow>
                    {expanded.includes(d.id) && (
                      <TableRow className="">
                        <TableCell colSpan={3} className="p-0 bg-purple-300">
                          <div className="p-4 bg-gray-200 border-t">
                            <div className="grid grid-cols-3 gap-4 mb-3">
                              <p className="text-left">
                                <strong>Current Truck</strong>
                              </p>
                              <p className="text-center">
                                <strong>Truck Model</strong>
                              </p>
                              <p className="text-center">
                                <strong>Status</strong>
                              </p>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mb-3">
                              <p className="text-left">{d.currentTruck || "None"}</p>
                              <p className="text-center">
                                {trucks.find(t => t.id === d.currentTruck)?.model || "N/A"}
                              </p>
                              <p className="text-center">
                                {trucks.find(t => t.id === d.currentTruck)?.status || "N/A"}
                              </p>
                            </div>
                            <Button 
                              className="w-full"
                              onClick={(e) => handleEditClick(d, e)}
                            >
                              Edit
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white p-4 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Add / Edit Driver</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            type="text"
            {...register("id", { required: true })}
            placeholder="Driver ID"
          />
          <Input
            type="text"
            {...register("name", { required: true })}
            placeholder="Driver Name"
          />
          <select
            {...register("currentTruck", { required: false })}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value="">Select a truck (optional)</option>
            {trucks.map((truck) => (
              <option key={truck.id} value={truck.id}>
                {truck.id} - {truck.model}
              </option>
            ))}
          </select>
          <Input
            type="number"
            {...register("contact", { required: true })}
            placeholder="Contact Number"
          />
          <Button type="submit">Save</Button>
        </form>
      </Card>

      {/* Edit Modal */}
      <EditDriverModal
        driver={selectedDriver}
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveEdit}
      />
    </div>
  );
}